import React from "react";

import styles from "./ApiDocumentationPage.module.styl";

const InlineText = ({ text }) => {
  const value = String(text || "");
  const parts = value.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

export const MarkdownText = ({ text }) => {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const nodes = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (line.trim().startsWith("```")) {
      const code = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        code.push(lines[i]);
        i += 1;
      }
      if (i >= lines.length) {
        throw new Error("Unclosed Markdown code fence in generated API documentation");
      }
      i += 1;
      nodes.push(
        <pre key={`code-${i}`} className={styles.markdownCode}>
          <code>{code.join("\n")}</code>
        </pre>
      );
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const content = <InlineText text={heading[2]} />;
      if (level === 1) nodes.push(<h2 key={`h-${i}`}>{content}</h2>);
      if (level === 2) nodes.push(<h3 key={`h-${i}`}>{content}</h3>);
      if (level >= 3) nodes.push(<h4 key={`h-${i}`}>{content}</h4>);
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i += 1;
      }
      nodes.push(
        <ul key={`ul-${i}`}>
          {items.map((item, index) => (
            <li key={index}><InlineText text={item} /></li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      nodes.push(
        <ol key={`ol-${i}`}>
          {items.map((item, index) => (
            <li key={index}><InlineText text={item} /></li>
          ))}
        </ol>
      );
      continue;
    }

    const paragraph = [line.trim()];
    i += 1;
    while (
      i < lines.length
      && lines[i].trim()
      && !lines[i].trim().startsWith("```")
      && !/^(#{1,4})\s+/.test(lines[i])
      && !/^[-*]\s+/.test(lines[i])
      && !/^\d+\.\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i].trim());
      i += 1;
    }

    nodes.push(
      <p key={`p-${i}`}>
        <InlineText text={paragraph.join(" ")} />
      </p>
    );
  }

  return <div className={styles.markdown}>{nodes}</div>;
};

export default MarkdownText;
